import React, { useEffect, useState } from 'react';
import { searchStartups, compareStartups } from '../api';
import RadarChart from '../components/RadarChart';
import { ScoreGauge } from '../components/ScoreGauge';

const CLUSTER_LABELS = ['BP', 'Ratio', 'Solidité Fi.', 'Maturité Fi.', 'Marché', 'Innovation',
  'Équipe', 'Org. Comm.', 'Notoriété', 'RSE', 'RGPD', 'Conformité'];
const CLUSTER_KEYS = ['cl01_bp','cl02_ratio','cl03_solidite_fi','cl04_maturite_fi','cl05_marche',
  'cl06_innovation','cl07_equipe','cl08_org_comm','cl09_notoriete','cl10_rse',
  'cl11_rgpd','cl12_conformite'];
const COLORS = ['#2E75B6', '#E67E22', '#27AE60', '#E74C3C'];

export default function ComparePage() {
  const [allStartups, setAllStartups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [compared, setCompared] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    searchStartups({ limit: 100 }).then(r => setAllStartups(r.data)).catch(console.error);
  }, []);

  const toggleSelect = (id) => {
    if (selected.includes(id)) setSelected(selected.filter(s => s !== id));
    else if (selected.length < 4) setSelected([...selected, id]);
  };

  const handleCompare = async () => {
    if (selected.length < 2) return;
    const data = await compareStartups(selected);
    setCompared(data);
  };

  const filtered = allStartups.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: 8 }}>Comparer des Startups</h1>
        <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14 }}>
          Sélectionnez 2 à 4 startups pour les comparer côte-à-côte.
        </p>

        {compared.length === 0 ? (
          <>
            <input type="text" placeholder="Filtrer par nom..." value={search} onChange={e => setSearch(e.target.value)}
              className="form-input" style={{ maxWidth: 400, marginBottom: 16 }} />
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: 'var(--text-light)' }}>{selected.length}/4 sélectionnées</span>
              {selected.length >= 2 && (
                <button onClick={handleCompare} className="btn btn-accent" style={{ fontSize: 13 }}>Comparer</button>
              )}
            </div>
            <div style={{ display: 'grid', gap: 8, maxHeight: 500, overflowY: 'auto' }}>
              {filtered.map(s => (
                <div key={s.id} onClick={() => toggleSelect(s.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    border: `2px solid ${selected.includes(s.id) ? 'var(--primary-light)' : 'var(--border)'}`,
                    borderRadius: 8, cursor: 'pointer', background: selected.includes(s.id) ? '#E3F2FD' : 'white' }}>
                  <input type="checkbox" checked={selected.includes(s.id)} readOnly style={{ width: 18, height: 18 }} />
                  <div style={{ flex: 1 }}>
                    <strong>{s.name}</strong>
                    <span className="badge badge-secteur" style={{ marginLeft: 8, fontSize: 10 }}>{s.categorie}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: parseFloat(s.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                    {s.note_sectorielle ? parseFloat(s.note_sectorielle).toFixed(1) : '-'}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setCompared([])} className="btn btn-outline" style={{ marginBottom: 24 }}>
              &larr; Nouvelle comparaison
            </button>

            {/* Score gauges row */}
            <div className="compare-grid" style={{ marginBottom: 32 }}>
              {compared.map((s, i) => (
                <div key={s.id} className="card" style={{ textAlign: 'center', borderTop: `4px solid ${COLORS[i]}` }}>
                  <h3 style={{ marginBottom: 8 }}>{s.name}</h3>
                  <span className="badge badge-secteur">{s.categorie}</span>
                  <div style={{ margin: '16px auto' }}>
                    <ScoreGauge score={s.note_sectorielle} size="lg" />
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Pivot: {parseFloat(s.note_pivot).toFixed(2)}</div>
                </div>
              ))}
            </div>

            {/* Radar overlay */}
            <div className="card" style={{ marginBottom: 32 }}>
              <h3 style={{ marginBottom: 16 }}>Radars superposés</h3>
              <div style={{ height: 450, position: 'relative' }}>
                <RadarChart
                  labels={CLUSTER_LABELS}
                  datasets={compared.map((s, i) => ({
                    label: s.name,
                    data: CLUSTER_KEYS.map(k => parseFloat(s[k]) || 0),
                    backgroundColor: COLORS[i] + '20',
                    borderColor: COLORS[i],
                    borderWidth: 2,
                    pointBackgroundColor: COLORS[i],
                    pointRadius: 4,
                  }))}
                  height={450}
                />
              </div>
            </div>

            {/* Comparison table */}
            <div className="card">
              <h3 style={{ marginBottom: 16 }}>Tableau comparatif</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: 10, textAlign: 'left' }}>Critère</th>
                      {compared.map((s, i) => (
                        <th key={s.id} style={{ padding: 10, textAlign: 'center', color: COLORS[i] }}>{s.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border)', fontWeight: 700 }}>
                      <td style={{ padding: 10 }}>Note ISI</td>
                      {compared.map(s => <td key={s.id} style={{ padding: 10, textAlign: 'center' }}>{parseFloat(s.note_sectorielle).toFixed(2)}</td>)}
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border)', fontWeight: 700 }}>
                      <td style={{ padding: 10 }}>Note Pivot</td>
                      {compared.map(s => <td key={s.id} style={{ padding: 10, textAlign: 'center' }}>{parseFloat(s.note_pivot).toFixed(2)}</td>)}
                    </tr>
                    {CLUSTER_LABELS.map((label, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 10 }}>{label}</td>
                        {compared.map(s => {
                          const v = parseFloat(s[CLUSTER_KEYS[idx]]);
                          const color = v > 7 ? '#27AE60' : v >= 4 ? '#F39C12' : '#E74C3C';
                          return <td key={s.id} style={{ padding: 10, textAlign: 'center', color, fontWeight: 600 }}>{v ? v.toFixed(1) : '-'}</td>;
                        })}
                      </tr>
                    ))}
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: 10 }}>Secteur</td>
                      {compared.map(s => <td key={s.id} style={{ padding: 10, textAlign: 'center' }}>{s.categorie}</td>)}
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: 10 }}>CA</td>
                      {compared.map(s => <td key={s.id} style={{ padding: 10, textAlign: 'center' }}>{s.ca || '-'}</td>)}
                    </tr>
                    <tr>
                      <td style={{ padding: 10 }}>Clientèle</td>
                      {compared.map(s => <td key={s.id} style={{ padding: 10, textAlign: 'center' }}>{s.adressage_client || '-'}</td>)}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
